import { useState, useEffect, useRef, useCallback } from 'react';
import { getRulesetType, updateRulesetType } from '../services/api/rulesetApi';

/**
 * Custom hook for managing auto-export toggle settings on a rule set.
 * Fetches the current rule set data and provides toggle handlers that
 * persist changes via the existing PUT /api/ruleset-types/{id} endpoint.
 *
 * @param {string} rulesetId - The ID of the rule set
 * @returns {Object} Auto-export settings state and controls
 */
export const useAutoExportSettings = (rulesetId) => {
  const [autoExportOnNoDiscrepancy, setAutoExportOnNoDiscrepancy] = useState(false);
  const [autoExportOnDiscrepancy, setAutoExportOnDiscrepancy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const rulesetRef = useRef(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    hasFetched.current = false;
  }, [rulesetId]);

  useEffect(() => {
    if (!hasFetched.current && rulesetId) {
      hasFetched.current = true;
      fetchSettings();
    }
  }, [rulesetId]);

  const fetchSettings = async () => {
    if (!rulesetId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getRulesetType(rulesetId);
      const data = response.data;
      rulesetRef.current = data;
      setAutoExportOnNoDiscrepancy(data.autoExportOnNoDiscrepancy ?? false);
      setAutoExportOnDiscrepancy(data.autoExportOnDiscrepancy ?? false);
    } catch (err) {
      setError('Failed to load auto-export settings');
      console.error('Error fetching auto-export settings:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggle a specific auto-export setting and persist via API.
   * On failure, reverts the toggle to its previous state.
   */
  const toggleSetting = useCallback(async (field, newValue) => {
    if (!rulesetRef.current || saving) return;

    const previousNoDisc = autoExportOnNoDiscrepancy;
    const previousOnDisc = autoExportOnDiscrepancy;

    // Optimistically update UI
    if (field === 'autoExportOnNoDiscrepancy') {
      setAutoExportOnNoDiscrepancy(newValue);
    } else {
      setAutoExportOnDiscrepancy(newValue);
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updatedRuleset = {
        ...rulesetRef.current,
        [field]: newValue
      };
      const response = await updateRulesetType(rulesetRef.current.id, updatedRuleset);
      rulesetRef.current = response.data;
      setSuccess('Auto-export setting updated successfully');

      // Clear success after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      // Revert on failure
      setAutoExportOnNoDiscrepancy(previousNoDisc);
      setAutoExportOnDiscrepancy(previousOnDisc);
      setError('Failed to update auto-export setting');
      console.error('Error updating auto-export setting:', err);
    } finally {
      setSaving(false);
    }
  }, [autoExportOnNoDiscrepancy, autoExportOnDiscrepancy, saving]);

  const clearError = useCallback(() => setError(null), []);
  const clearSuccess = useCallback(() => setSuccess(null), []);

  return {
    autoExportOnNoDiscrepancy,
    autoExportOnDiscrepancy,
    loading,
    saving,
    error,
    success,
    toggleSetting,
    clearError,
    clearSuccess
  };
};
